import { Helmet } from 'react-helmet-async';

const SEO = ({
  title = 'Quartermaster by MindMade - AI-Powered Platform for Personal & Business Automation',
  description = 'Build custom AI agents with a visual IDE, automate workflows, and integrate with leading LLM providers like GPT-4, Claude, and Groq. A MindMade product available on web, mobile, and desktop.',
  path = '',
  imageUrl = '/linuxIconBlack128.png',
  type = 'website',
  articleMeta = null,
}) => {
  const siteUrl = 'https://quartermaster.io';
  const companyUrl = 'https://mindmade.io';
  const fullUrl = `${siteUrl}${path}`;
  const fullImageUrl = `${siteUrl}${imageUrl}`;

  // Enhanced schema markup for software platform
  const platformSchema = {
    '@context': 'https://schema.org',
    '@type': 'SoftwareApplication',
    name: 'Quartermaster',
    applicationCategory: 'Artificial Intelligence Platform',
    // "operatingSystem": "Android, iOS, Windows, macOS, Linux, Web",
    operatingSystem: 'Windows, macOS, Linux, Web',
    offers: {
      '@type': 'Offer',
      price: '0',
      priceCurrency: 'EUR',
      description: 'Start building for free',
      priceValidUntil: '2025-12-31',
    },
    description:
      'Comprehensive AI platform for building custom agents and automating workflows',
    aggregateRating: {
      '@type': 'AggregateRating',
      ratingValue: '4.8',
      ratingCount: '2000',
    },
    downloadUrl: 'https://quartermaster.io/download',
    softwareVersion: '1.0.0',
    author: {
      '@type': 'Organization',
      name: 'MindMade',
      url: companyUrl,
    },
    // offers: [
    //   {
    //     '@type': 'Offer',
    //     name: 'Free Plan',
    //     price: '0',
    //     priceCurrency: 'EUR',
    //   },
    //   {
    //     '@type': 'Offer',
    //     name: 'Basic Plan',
    //     price: '13.99',
    //     priceCurrency: 'EUR',
    //   },
    //   {
    //     '@type': 'Offer',
    //     name: 'Dev Plan',
    //     price: '63.99',
    //     priceCurrency: 'EUR',
    //   },
    //   {
    //     '@type': 'Offer',
    //     name: 'Pro Plan',
    //     price: '119.99',
    //     priceCurrency: 'EUR',
    //   },
    // ],
  };

  // Parent Organization schema
  const parentOrganizationSchema = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: 'MindMade',
    url: companyUrl,
    logo: `${companyUrl}/logo.png`,
    sameAs: [
      'https://github.com/MindMadeLab',
      'https://x.com/MindMadeLab',
      // 'https://linkedin.com/company/mindmade',
    ],
    contactPoint: {
      '@type': 'ContactPoint',
      contactType: 'customer support',
      email: 'info@mindmade.io',
    },
  };

  // Product Organization schema
  const productOrganizationSchema = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: 'Quartermaster',
    url: siteUrl,
    logo: `${siteUrl}/linuxIconBlack128.png`,
    parentOrganization: {
      '@type': 'Organization',
      name: 'MindMade',
      url: companyUrl,
    },
    potentialAction: {
      '@type': 'DownloadAction',
      target: {
        '@type': 'EntryPoint',
        urlTemplate: 'https://quartermaster.io/download/',
        actionPlatform: [
          'http://schema.org/DesktopWebPlatform',
          // 'http://schema.org/IOSPlatform',
          // 'http://schema.org/AndroidPlatform',
        ],
      },
    },
  };

  return (
    <Helmet>
      {/* Basic Meta Tags */}
      <title>{title}</title>
      <meta name="description" content={description} />
      <meta
        name="viewport"
        content="width=device-width, initial-scale=1, maximum-scale=5"
      />
      <meta name="theme-color" content="#000000" />

      {/* Enhanced Keywords */}
      <meta
        name="keywords"
        content="MindMade, Quartermaster, AI platform, automation software, AI agents, visual IDE, LLM integration, GPT-4, Claude, Groq, cross-platform automation, workflow automation, AI development, personal AI assistant, business automation"
      />

      {/* Platform Specific Meta Tags */}
      {/* <meta name="apple-itunes-app" content="app-id=yourAppId" />
      <meta
        name="google-play-app"
        content="app-id=com.mindmade.quartermaster"
      />
      <meta name="msApplication-ID" content="Quartermaster" />
      <meta
        name="msApplication-PackageFamilyName"
        content="MindMade.Quartermaster_id"
      /> */}

      {/* Open Graph Meta Tags */}
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={fullImageUrl} />
      <meta property="og:url" content={fullUrl} />
      <meta property="og:type" content={type} />
      <meta property="og:site_name" content="Quartermaster by MindMade" />
      <meta property="og:app_id" content="your_fb_app_id" />

      {/* Article Specific Meta Tags */}
      {articleMeta && (
        <>
          <meta
            property="article:published_time"
            content={articleMeta.publishedTime}
          />
          <meta
            property="article:modified_time"
            content={articleMeta.modifiedTime}
          />
          <meta property="article:author" content={articleMeta.author} />
          <meta property="article:section" content={articleMeta.section} />
          {articleMeta.tags.map((tag) => (
            <meta property="article:tag" content={tag} key={tag} />
          ))}
        </>
      )}

      {/* Twitter Card Meta Tags */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:site" content="@MindMadeLab" />
      <meta name="twitter:creator" content="@MindMadeLab" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={fullImageUrl} />
      <meta name="twitter:app:country" content="US" />
      <meta name="twitter:app:name:iphone" content="Quartermaster" />
      {/* <meta name="twitter:app:id:iphone" content="your_ios_app_id" /> */}
      {/* <meta name="twitter:app:name:googleplay" content="Quartermaster" /> */}
      {/* <meta
        name="twitter:app:id:googleplay"
        content="com.mindmade.quartermaster"
      /> */}

      {/* App Links Meta Tags */}
      {/* <meta property="al:ios:url" content="quartermaster://home" />
      <meta property="al:ios:app_store_id" content="your_ios_app_id" />
      <meta property="al:ios:app_name" content="Quartermaster" />
      <meta property="al:android:url" content="quartermaster://home" />
      <meta
        property="al:android:package"
        content="com.mindmade.quartermaster"
      />
      <meta property="al:android:app_name" content="Quartermaster" /> */}

      {/* Publisher Meta Tags */}
      <meta name="publisher" content="MindMade" />
      <link rel="publisher" href={companyUrl} />

      {/* PWA Tags */}
      <link rel="manifest" href="/manifest.json" />
      <meta name="mobile-web-app-capable" content="yes" />
      <meta name="apple-mobile-web-app-capable" content="yes" />
      <meta name="apple-mobile-web-app-status-bar-style" content="black" />
      <meta name="apple-mobile-web-app-title" content="Quartermaster" />

      {/* Icons */}
      <link rel="icon" href="/icon1.ico" />
      <link rel="apple-touch-icon" href="/linuxIconBlack128.png" />

      {/* Preconnect to External Resources */}
      <link rel="preconnect" href="https://fonts.googleapis.com" />
      <link
        rel="preconnect"
        href="https://fonts.gstatic.com"
        crossOrigin="true"
      />
      <link rel="preconnect" href="https://mindmade.io" />

      {/* Schema.org JSON-LD */}
      <script type="application/ld+json">
        {JSON.stringify(platformSchema)}
      </script>
      <script type="application/ld+json">
        {JSON.stringify(parentOrganizationSchema)}
      </script>
      <script type="application/ld+json">
        {JSON.stringify(productOrganizationSchema)}
      </script>

      {/* Security Headers */}
      <meta
        httpEquiv="Content-Security-Policy"
        content="default-src 'self' https:; script-src 'self' 'unsafe-inline' 'unsafe-eval' https:; style-src 'self' 'unsafe-inline' https:; img-src 'self' data: http://localhost:8000 https:; font-src 'self' data: https:; connect-src 'self' http://localhost:8000 https://release.quartermaster.io https://api.prod.quartermaster.io https://cdn.quartermaster.io wss://api.prod.quartermaster.io https: wss:;"
      />
      <meta httpEquiv="X-Content-Type-Options" content="nosniff" />

      {/* Additional Meta Tags */}
      <meta name="format-detection" content="telephone=no" />
      <meta name="msapplication-tap-highlight" content="no" />

      {/* Language and Region */}
      <meta property="og:locale" content="en_US" />
      <link rel="alternate" href={siteUrl} hrefLang="x-default" />
      <link rel="alternate" href={siteUrl} hrefLang="en" />
    </Helmet>
  );
};

export default SEO;
